
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { MapPin, CheckCircle2, Clock, Users, Building2 } from 'lucide-react';
import { StructuredData } from '@/components/seo/StructuredData';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { FAQSchema } from '@/components/seo/FAQSchema';
import { ServiceSchema } from '@/components/seo/ServiceSchema';
import { InternalLinks } from '@/components/seo/InternalLinks';

interface CityPageProps {
  city: string;
  state: string;
  stateAbbr: string;
  population: string;
  majorIndustries: string[];
  businessChallenges: string[];
  peoSolutions: string[];
  localBusinessTypes: { name: string; description: string }[];
  metaDescription: string;
  keyFeatures: { icon: React.ComponentType<any>; title: string; description: string }[];
}

export const CityPageTemplate = ({
  city,
  state,
  stateAbbr,
  population,
  majorIndustries,
  businessChallenges,
  peoSolutions,
  localBusinessTypes,
  metaDescription,
  keyFeatures
}: CityPageProps) => {
  const breadcrumbItems = [
    { name: 'Cities', url: '/cities' },
    { name: state, url: `/states/${state.toLowerCase().replace(' ', '')}` },
    { name: city, url: `/cities/${city.toLowerCase().replace(' ', '-')}-${stateAbbr.toLowerCase()}` }
  ];

  const localBusinessSchema = {
    name: `The PEO App - ${city}`,
    address: {
      "@type": "PostalAddress",
      "addressLocality": city,
      "addressRegion": stateAbbr,
      "addressCountry": "US"
    },
    areaServed: {
      "@type": "City",
      "name": city,
      "containedInPlace": {
        "@type": "State",
        "name": state
      }
    },
    serviceType: "Professional Employer Organization Services"
  };

  const cityFAQs = [
    {
      question: `What is a PEO and how can it help my ${city} business?`,
      answer: `A Professional Employer Organization (PEO) provides comprehensive HR services to ${city} businesses, including payroll, benefits, workers' compensation, and compliance support. PEOs help local businesses reduce costs, minimize risk, and focus on growth.`
    },
    {
      question: `How much does PEO service cost in ${city}?`,
      answer: `PEO costs in ${city} typically range from 2-8% of payroll, depending on services needed and company size. The savings from better benefits rates, reduced workers' comp costs, and HR efficiency often offset the fees.`
    },
    {
      question: `What industries do ${city} PEOs serve?`,
      answer: `${city} PEOs serve diverse industries including ${majorIndustries.slice(0, 3).join(', ')}, and many others. They provide specialized expertise for industry-specific compliance and workforce management needs.`
    },
    {
      question: `How do I choose the right PEO for my ${city} business?`,
      answer: `Consider factors like industry expertise, service offerings, technology platforms, client references, and pricing. The PEO App helps ${city} businesses compare multiple PEO providers to find the best fit.`
    }
  ];

  const relatedLinks = [
    {
      title: `${state} PEO Services`,
      url: `/states/${state.toLowerCase().replace(' ', '')}`,
      description: `Explore PEO options throughout ${state}`
    },
    {
      title: "PEO Shopping Guide",
      url: "/peo-shopping-guide",
      description: "Complete guide to selecting the right PEO"
    },
    {
      title: "Industry-Specific PEO Solutions",
      url: "/industries",
      description: "Find PEOs specialized for your industry"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
      <Helmet>
        <title>{city} PEO Services | Find Top PEOs in {city}, {stateAbbr} | The PEO App</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://thepeoapp.com/cities/${city.toLowerCase().replace(' ', '-')}-${stateAbbr.toLowerCase()}`} />
        <meta name="keywords" content={`${city} PEO, ${stateAbbr} Professional Employer Organization, PEO ${city}, HR services ${city}, payroll services ${city}, employee benefits ${city}`} />
        
        <meta name="geo.region" content={`US-${stateAbbr}`} />
        <meta name="geo.placename" content={city} />
        
        <meta property="og:title" content={`${city} PEO Services | Find Top PEOs in ${city}, ${stateAbbr}`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://thepeoapp.com/cities/${city.toLowerCase().replace(' ', '-')}-${stateAbbr.toLowerCase()}`} />
        <meta property="og:image" content="https://thepeoapp.com/og-image.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${city} PEO Services | Find Top PEOs in ${city}, ${stateAbbr}`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://thepeoapp.com/og-image.png" />
      </Helmet>
      
      <StructuredData type="LocalBusiness" data={localBusinessSchema} />
      <ServiceSchema 
        serviceName={`PEO Services in ${city}`}
        description={`Professional Employer Organization services for ${city} businesses`}
        provider="The PEO App"
        areaServed={city}
      />
      <FAQSchema faqs={cityFAQs} />
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />
          
          <div className="flex items-center mb-6">
            <MapPin className="h-6 w-6 text-[#00a86b] mr-2" />
            <p className="text-sm text-gray-600">{city}, {state}</p>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {city} PEO Services
          </h1>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              {city} businesses across {majorIndustries.join(', ')} industries trust Professional Employer Organizations to streamline HR operations, reduce costs, and ensure compliance. With a metro population of {population}, {city} offers diverse business opportunities that PEOs help companies navigate successfully.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {keyFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                  <IconComponent className="h-10 w-10 text-[#00a86b] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">{city} Business Challenges PEOs Address</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">Common Challenges</h3>
                <ul className="space-y-3">
                  {businessChallenges.map((challenge, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">PEO Solutions</h3>
                <ul className="space-y-3">
                  {peoSolutions.map((solution, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-[#00a86b] mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{solution}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find {city} PEO Providers</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand {city}'s business environment and have local expertise to help your company succeed.
            </p>
            <Link to="/signup">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get {city} PEO Quotes
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-50 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-6">{city} Business Types We Serve</h2>
              <div className="grid gap-4">
                {localBusinessTypes.map((business, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg">
                    <h3 className="font-semibold">{business.name}</h3>
                    <p className="text-sm text-gray-600">{business.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <InternalLinks 
              title="Related Resources"
              links={relatedLinks}
            />
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {cityFAQs.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
